import React from 'react';

import LoadingSpinner from '../ui/LoadingSpinner';
import ImageSelector from '../ui/ImageSelector';
import { useSupabaseContent } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { AboutContent, defaultAboutContent } from '../../lib/supabase';
import { seoConfig } from '../../lib/seo';
import { createImageData } from '../../lib/thumbnails';

// Utility function to render multi-paragraph text
const renderParagraphs = (text: string, className?: string) => {
  return text
    .split('\n\n') // Split by double line breaks
    .filter(paragraph => paragraph.trim().length > 0) // Remove empty paragraphs
    .map((paragraph, index) => (
      <p key={index} className={className}>
        {paragraph.trim()}
      </p>
    ));
};

const About: React.FC = () => {
  useCanonicalUrl(seoConfig.about);

  const { data: content, loading, error } = useSupabaseContent<AboutContent>(
    'about_content',
    defaultAboutContent,
    true
  );

  // LUT Roll images for the ImageSelector
  const lutRollImages = createImageData([
    {
      id: 'jakarta-haze',
      src: '/assets/imageSelector/jakartaHaze.jpg',
      label: 'Jakarta haze',
      alt: 'Jakarta haze LUT filter preview'
    },
    {
      id: 'auckland-edge',
      src: '/assets/imageSelector/aucklandEdge.jpg',
      label: 'Auckland edge',
      alt: 'Auckland edge LUT filter preview'
    },
    {
      id: 'havana-patina',
      src: '/assets/imageSelector/havanaPatina.jpg',
      label: 'Havana patina',
      alt: 'Havana patina LUT filter preview'
    },
    {
      id: 'el-paso-heat',
      src: '/assets/imageSelector/elPasoHeat.jpg',
      label: 'El Paso heat',
      alt: 'El Paso heat LUT filter preview'
    },
    {
      id: 'bangkok-tilt',
      src: '/assets/imageSelector/bangkokTilt.jpg',
      label: 'Bangkok tilt',
      alt: 'Bangkok tilt LUT filter preview'
    }
  ]);

  // Show loading when we don't have any content yet (first visit with no cache)
  if (loading && !content) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading content..." />
        </main>
      </div>
    );
  }

  // Use content (cached, from Supabase, or fallback)
  const aboutData = content || defaultAboutContent;

  return (
    <div className="page-container">
      <main className="page-main">
        {/* Show subtle indicator only if using fallback (not cached) */}
        {error && !content && (
          <div className="content-notice">
            <small>📡 Content loaded from defaults</small>
          </div>
        )}
        
        <header className="page-header">
          <h1>{aboutData.title}</h1>
          <div className="page-description">
            {renderParagraphs(aboutData.description, 'page-description')}
          </div>
        </header>

        <div className="page-content">
          <div className="content-section">
            <ImageSelector 
              images={lutRollImages}
              className="about-lut-selector"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default About; 